/**
 * D98.3 / D98.5 / D98.8 - the ONE rule for "what is wrong with this client invoice".
 *
 * Replaces, once wired in: CIP's blockingLineItems, getRowSignals chips (Missing item, Qty needed,
 * Short qty, Bill short, Discrepancy), the unpriced check (D96.6/.8), the discrepancy box and the
 * Verification panel's builder (computeInvoiceVerification: Phase 1/2 + J6/J7/J8 line alerts).
 * Pure: no React, no I/O.
 *
 * severity: 'block' = cannot Mark sent; 'warn' = shown, never blocks; 'info' = a fact the bar
 * counts ("n not supplied"). `alertKey` is set only on issues the J8 alert history records.
 */
import type { ClientInvoiceItemDto } from '../../lib/clientInvoiceApi';
import { num } from '../../utils/invoiceLineMath';
import { billedQuantity, isNotSuppliedLine } from '../../utils/clientInvoiceLine';

export type IssueSeverity = 'block' | 'warn' | 'info';
export type IssueCode =
  | 'missing_name'
  | 'qty_needed'
  | 'unpriced'
  | 'supplier_not_picked'
  | 'not_supplied'
  | 'discrepancy'
  | 'short_vs_order'
  | 'markup_negative'
  | 'markup_high'
  | 'order_total';

export type LineIssue = {
  code: IssueCode;
  severity: IssueSeverity;
  message: string;
  /** Set when the J8 alert history records this issue (same key CIP's computeAlertKey built). */
  alertKey?: string;
  alertLevel?: 'warning' | 'error';
};

/** Same shape CIP's InvoiceAlert has, so syncClientInvoiceAlerts keeps receiving what it always did. */
export type InvoiceAlert = { level: 'info' | 'warning' | 'error'; lineNumber?: number; message: string; alertKey: string };

/** J6: billed vs confirmed (or shipped, when more shipped, D157.6) quantity tolerance. The ONE short-against-the-order
 *  rule (the old "Short qty" chip fired on any shortfall; it is gone, this 5% rule feeds the saved alert history). */
export const QTY_TOLERANCE_PCT = 5;
export const MARKUP_HIGH_THRESHOLD = 100;

// Same as CIP's computeAlertKey: level|lineNumber|normalised message. Stored history depends on it.
export function computeAlertKey(a: { level: string; lineNumber?: number | null; message: string }): string {
  return [a.level, a.lineNumber ?? '', a.message.replace(/\s+/g, ' ').trim()].join('|');
}

// Missing counts as the fallback here: a missing quantity is "needed", as before.
const n = (value: unknown, fallback = 0): number => num(value) ?? fallback;

const lineNo = (item: ClientInvoiceItemDto, index: number) => item.lineNumber ?? index + 1;

function alertIssue(
  base: Omit<LineIssue, 'alertKey' | 'alertLevel'>,
  level: 'warning' | 'error',
  lineNumber?: number
): LineIssue {
  return { ...base, alertLevel: level, alertKey: computeAlertKey({ level, lineNumber, message: base.message }) };
}

export function lineIssuesFor(item: ClientInvoiceItemDto, index: number, unpicked: boolean): LineIssue[] {
  const out: LineIssue[] = [];
  const no = lineNo(item, index);
  const hasName = Boolean(String(item.itemName || item.itemDescription || '').trim());
  const confirmed = n(item.confirmedQuantity);
  const billed = billedQuantity(item) ?? 0;
  // D98.5 / D170.7: an order line, named, ordered but billed 0 was not supplied: it goes out, priced or not.
  const notSupplied = isNotSuppliedLine(item);

  if (!hasName) out.push({ code: 'missing_name', severity: 'block', message: `Line ${no}: item name is missing` });
  if (notSupplied) {
    out.push({ code: 'not_supplied', severity: 'info', message: `Line ${no}: not supplied` });
  } else {
    // D170.7: say what to do; Remove keeps an order line as Not supplied and deletes a hand-added one.
    if (billed <= 0) {
      out.push({ code: 'qty_needed', severity: 'block', message: `Line ${no}: billed quantity is needed: bill it or remove the line` });
    }
    // D96.6/.8: no selling price (0 is a price, free of charge) cannot be sent.
    if (item.invoiceLineTotal == null) {
      out.push({ code: 'unpriced', severity: 'block', message: `Line ${no}: no selling price` });
    }
  }
  if (unpicked) {
    out.push({ code: 'supplier_not_picked', severity: 'block', message: `Line ${no}: pick the supplier from the vendor list` });
  }
  const discrepancy = String(item.discrepancyAlert || '').trim();
  if (discrepancy) out.push({ code: 'discrepancy', severity: 'warn', message: `Line ${no}: ${discrepancy}` });

  if (confirmed > 0 && !notSupplied) {
    // D157.6: a slip may ship past the order and bill it, so billed is held to the larger of confirmed and shipped.
    const shipped = Math.max(0, n(item.dispatchedQuantity));
    const bySlips = shipped > confirmed;
    const against = bySlips ? shipped : confirmed;
    const pct = Math.abs(((billed - against) / against) * 100);
    if (pct > QTY_TOLERANCE_PCT) {
      out.push(
        alertIssue(
          {
            code: 'short_vs_order',
            severity: 'warn',
            message: `Line ${no}: billed qty (${billed}) differs from ${bySlips ? 'shipped' : 'confirmed'} qty (${against}) by ${pct.toFixed(1)}% (>5%)`,
          },
          'warning',
          no
        )
      );
    }
  }
  // D119.19: a line billed 0 shows its unit mark-up % but raises no mark-up warning.
  if (billed > 0 && item.markupPercent != null) {
    const mp = n(item.markupPercent, NaN);
    if (Number.isFinite(mp) && mp < 0) {
      out.push(
        alertIssue(
          { code: 'markup_negative', severity: 'warn', message: `Line ${no}: NEGATIVE mark-up (${mp.toFixed(2)}%) — selling below cost` },
          'error',
          no
        )
      );
    } else if (Number.isFinite(mp) && mp > MARKUP_HIGH_THRESHOLD) {
      out.push(
        alertIssue({ code: 'markup_high', severity: 'warn', message: `Line ${no}: unusually high mark-up (${mp.toFixed(2)}%)` }, 'warning', no)
      );
    }
  }
  return out;
}

export type OrderTotalInput = {
  /** The invoice grand total (features/clientInvoice/invoiceTotals), null when not known. */
  invoiceGrandTotal: number | null | undefined;
  invoiceCurrency: string | null | undefined;
  /** The confirmed order's totalQuote: the quotation's final price after discount and charges. */
  orderTotal: number | null | undefined;
  orderCurrency: string | null | undefined;
};

/** D98.8. Tolerance is the old Phase 1 one: the larger of 0.50 and 0.1% of the order total. */
export function orderTotalIssue(input: OrderTotalInput | null | undefined): LineIssue | null {
  if (!input) return null;
  // ponytail: no conversion across currencies - differing or unknown currencies raise nothing;
  // upgrade by pinning an FX rate on the confirmed order if a mixed-currency check is ever wanted.
  const ic = String(input.invoiceCurrency || '').trim().toUpperCase();
  const oc = String(input.orderCurrency || '').trim().toUpperCase();
  if (!ic || ic !== oc) return null;
  if (input.invoiceGrandTotal == null || input.orderTotal == null) return null;
  const total = n(input.invoiceGrandTotal, NaN);
  const order = n(input.orderTotal, NaN);
  if (!Number.isFinite(total) || !Number.isFinite(order) || order <= 0) return null;
  if (Math.abs(total - order) <= Math.max(0.5, order * 0.001)) return null;
  const delta = Number((total - order).toFixed(2));
  return alertIssue(
    {
      code: 'order_total',
      severity: 'warn',
      message: `Invoice total (${ic} ${total.toFixed(2)}) does not match the confirmed order total (${ic} ${order.toFixed(2)}). Difference: ${delta.toFixed(2)}`,
    },
    'error'
  );
}

export type InvoiceIssues = {
  /** One list per line, same order as items. */
  lines: LineIssue[][];
  /** The line number shown for each line (lineNumber, else position + 1). */
  lineNumbers: number[];
  /** Invoice-level issues (the order-total check). */
  invoice: LineIssue[];
};

/**
 * D86: the line numbers whose supplier (the line's, or Vendor 1/2/3) has a name but no vendor id -
 * an old typed name nobody has picked yet. The server refuses to save or send them
 * (VENDOR_NOT_IN_LIST). The ONE owner of this rule (it used to live in the page).
 */
export function unpickedSupplierLines(items: ClientInvoiceItemDto[] | undefined): number[] {
  return (items || []).flatMap((it, index) => {
    const bad =
      (String(it.supplierName || '').trim() && !it.supplierVendorId) ||
      (it.vendorQuantities || []).some((vq) => String(vq.vendorName || '').trim() && !vq.vendorId);
    return bad ? [it.lineNumber ?? index + 1] : [];
  });
}

/** `unpickedLines` defaults to unpickedSupplierLines(items); pass it only to override. */
export function analyseInvoice(args: {
  items: ClientInvoiceItemDto[] | undefined;
  unpickedLines?: number[];
  orderTotal?: OrderTotalInput | null;
}): InvoiceIssues {
  const items = args.items || [];
  const unpicked = new Set(args.unpickedLines ?? unpickedSupplierLines(items));
  const lines = items.map((it, i) => lineIssuesFor(it, i, unpicked.has(lineNo(it, i))));
  const total = orderTotalIssue(args.orderTotal);
  return { lines, lineNumbers: items.map(lineNo), invoice: total ? [total] : [] };
}

export const hasBlockingIssue = (r: InvoiceIssues) => r.lines.some((l) => l.some((i) => i.severity === 'block'));

export type LineStatus = 'block' | 'warn' | 'done' | 'none';
/** The row's status dot: block > warn > done (completedTick) > none. */
export function lineStatus(issues: LineIssue[], completedTick: boolean | undefined): LineStatus {
  if (issues.some((i) => i.severity === 'block')) return 'block';
  if (issues.some((i) => i.severity === 'warn')) return 'warn';
  return completedTick ? 'done' : 'none';
}

/**
 * The alerts the J8 history sync posts: the J6/J7/J8 line alerts with the keys CIP built, plus the
 * D98.8 order-total alert. The old Phase 1/2 alerts are retired (their message, hence key, was the
 * raw-line-sum comparison D98.8 replaces), so the history gains a new key for that one check only.
 */
export function alertsOf(r: InvoiceIssues, items: ClientInvoiceItemDto[] | undefined): InvoiceAlert[] {
  const out: InvoiceAlert[] = [];
  const push = (i: LineIssue, lineNumber?: number) => {
    if (!i.alertKey || !i.alertLevel) return;
    out.push({ level: i.alertLevel, ...(lineNumber != null ? { lineNumber } : {}), message: i.message, alertKey: i.alertKey });
  };
  r.invoice.forEach((i) => push(i));
  r.lines.forEach((l, idx) => l.forEach((i) => push(i, lineNo((items || [])[idx] || {}, idx))));
  return out;
}
